'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { MessageCircle, Eye, PenSquare, AlertCircle, CheckCircle, Lock } from 'lucide-react'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import { useTheme } from '@/contexts/ThemeContext'
import logger from '@/utils/logger'
import { maskName } from '@/utils/privacy'
import { parseAuthToken } from '@/utils/base64'

interface Inquiry {
  id: string
  user_id: string
  title: string
  content: string
  category: string
  status: 'pending' | 'in_progress' | 'resolved'
  is_private: boolean
  created_at: string
  user_profiles: {
    name: string
  }
  view_count?: number
  comment_count?: number
}

const categoryLabels: Record<string, string> = {
  bug: '버그',
  feature: '기능 요청',
  question: '질문',
  other: '기타'
}

const statusLabels: Record<string, { text: string; lightColor: string; darkColor: string; icon: typeof AlertCircle }> = {
  pending: { text: '대기중', lightColor: 'text-yellow-600 bg-yellow-100', darkColor: 'text-yellow-400 bg-gray-700/80', icon: AlertCircle },
  in_progress: { text: '처리중', lightColor: 'text-blue-600 bg-blue-100', darkColor: 'text-blue-400 bg-gray-700/80', icon: AlertCircle },
  resolved: { text: '답변완료', lightColor: 'text-green-600 bg-green-100', darkColor: 'text-green-400 bg-gray-700/80', icon: CheckCircle }
}

export default function InquiryBoardPage() {
  const { theme } = useTheme()
  const [inquiries, setInquiries] = useState<Inquiry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)

  const isDark = theme === 'dark'

  useEffect(() => {
    // 현재 로그인 유저 확인 - JWT 토큰에서 kakao_id/naver_id를 파싱하여 DB에서 user_id 조회
    const loadCurrentUser = async () => {
      const token = localStorage.getItem('auth_token')
      if (token) {
        const payload = parseAuthToken(token)
        if (payload) {
          const supabase = getSupabaseClient()
          let userQuery = supabase.from('user_profiles').select('id')
          if (payload.kakao_id) {
            userQuery = userQuery.eq('kakao_id', payload.kakao_id)
          } else if (payload.naver_id) {
            userQuery = userQuery.eq('naver_id', payload.naver_id)
          }

          const { data: userData } = await userQuery.single()
          if (userData) {
            setCurrentUserId(userData.id)
          }
        }
      }
    }
    loadCurrentUser()
    loadInquiries()
  }, [])

  const loadInquiries = async () => {
    try {
      const supabase = getSupabaseClient()

      const { data, error } = await supabase
        .from('inquiries')
        .select(`
          *,
          user_profiles!inquiries_user_id_fkey (
            name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      setInquiries(data || [])
    } catch (error) {
      logger.error('문의글 로드 실패:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInMinutes < 1) return '방금 전'
    if (diffInMinutes < 60) return `${diffInMinutes}분 전`
    if (diffInHours < 24) return `${diffInHours}시간 전`
    if (diffInDays < 7) return `${diffInDays}일 전`
    return date.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-2xl mx-auto">
        {/* 헤더 */}
        <div className={`sub-sticky z-20 ${isDark ? 'bg-gray-900' : 'bg-white'} border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="px-3 xxs:px-2 py-2.5 xxs:py-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 xxs:gap-1">
                <Link
                  href="/community"
                  className={`text-sm xxs:text-xs ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  ←
                </Link>
                <h1 className={`text-base xxs:text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  문의게시판
                </h1>
                <span className={`text-[10px] xxs:text-[9px] px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                  {inquiries.length}
                </span>
              </div>
              <Link
                href="/community/inquiry/new"
                className={`flex items-center gap-1 px-2.5 xxs:px-2 py-1.5 xxs:py-1 text-xs xxs:text-[11px] font-medium rounded-lg transition-colors ${
                  isDark
                    ? 'bg-blue-600 hover:bg-blue-500 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                <PenSquare className="w-3.5 h-3.5 xxs:w-3 xxs:h-3" />
                문의하기
              </Link>
            </div>
          </div>
        </div>

        {/* 안내 문구 */}
        <div className={`mx-3 xxs:mx-2 mt-3 xxs:mt-2 p-2.5 xxs:p-2 rounded-lg text-xs xxs:text-[11px] ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
          💡 버그 제보, 기능 요청, 불편사항을 남겨주세요!
        </div>

        {/* 게시글 목록 */}
        <div className="pb-20">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 xxs:py-12">
              <div className={`text-xs xxs:text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                로딩 중...
              </div>
            </div>
          ) : inquiries.length === 0 ? (
            <div className={`mx-3 xxs:mx-2 mt-3 xxs:mt-2 rounded-xl p-6 xxs:p-4 text-center ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <p className={`text-xs xxs:text-[11px] mb-3 xxs:mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                아직 작성된 문의가 없습니다.
                <br />
                불편한 점이 있으시면 알려주세요!
              </p>
              <Link
                href="/community/inquiry/new"
                className="inline-flex items-center gap-1 px-3 xxs:px-2.5 py-1.5 xxs:py-1 text-xs xxs:text-[11px] font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <PenSquare className="w-3.5 h-3.5 xxs:w-3 xxs:h-3" />
                문의하기
              </Link>
            </div>
          ) : (
            <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
              {inquiries.map((inquiry) => {
                const status = statusLabels[inquiry.status] || statusLabels.pending
                const StatusIcon = status.icon
                const isOwner = currentUserId === inquiry.user_id
                const canView = !inquiry.is_private || isOwner

                // 비밀글이고 본인이 아니면 제한된 정보만 표시
                if (inquiry.is_private && !isOwner) {
                  return (
                    <div
                      key={inquiry.id}
                      className={`px-3 xxs:px-2 py-3 xxs:py-2.5 ${
                        isDark ? 'bg-gray-800/30' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 xxs:gap-1 mb-1.5 xxs:mb-1">
                        <span className={`text-[10px] xxs:text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                          isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                        }`}>
                          <Lock className="w-2.5 h-2.5" />
                          비밀글
                        </span>
                        <span className={`text-[10px] xxs:text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${isDark ? status.darkColor : status.lightColor}`}>
                          <StatusIcon className="w-2.5 h-2.5" />
                          {status.text}
                        </span>
                      </div>
                      <p className={`text-sm xxs:text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        🔒 비밀글입니다.
                      </p>
                      <div className={`flex items-center gap-1.5 xxs:gap-1 text-[10px] xxs:text-[9px] mt-1.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        <span>{maskName(inquiry.user_profiles?.name)}</span>
                        <span>·</span>
                        <span>{formatDate(inquiry.created_at)}</span>
                      </div>
                    </div>
                  )
                }

                return (
                  <Link
                    key={inquiry.id}
                    href={`/community/inquiry/${inquiry.id}`}
                    className={`block px-3 xxs:px-2 py-3 xxs:py-2.5 transition-colors ${
                      isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* 상단: 카테고리 + 상태 + 비밀글 */}
                    <div className="flex items-center gap-1.5 xxs:gap-1 mb-1.5 xxs:mb-1">
                      <span className={`text-[10px] xxs:text-[9px] px-1.5 py-0.5 rounded ${
                        isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {categoryLabels[inquiry.category] || inquiry.category}
                      </span>
                      <span className={`text-[10px] xxs:text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${isDark ? status.darkColor : status.lightColor}`}>
                        <StatusIcon className="w-2.5 h-2.5" />
                        {status.text}
                      </span>
                      {inquiry.is_private && (
                        <span className={`text-[10px] xxs:text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                          isDark ? 'bg-gray-700/80 text-orange-400' : 'bg-orange-100 text-orange-600'
                        }`}>
                          <Lock className="w-2.5 h-2.5" />
                          비밀
                        </span>
                      )}
                    </div>

                    {/* 제목 + NEW 뱃지 */}
                    <div className="flex items-start gap-1.5 mb-1.5 xxs:mb-1">
                      <h3 className={`flex-1 text-sm xxs:text-xs font-medium leading-snug line-clamp-2 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {inquiry.title}
                      </h3>
                    </div>

                    {/* 본문 미리보기 (1줄) */}
                    <p className={`text-xs xxs:text-[11px] line-clamp-1 mb-2 xxs:mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {inquiry.content}
                    </p>

                    {/* 하단: 작성자 | 시간 | 조회 | 댓글 */}
                    <div className={`flex items-center gap-1.5 xxs:gap-1 text-[10px] xxs:text-[9px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                        {maskName(inquiry.user_profiles?.name)}
                      </span>
                      <span>·</span>
                      <span>{formatDate(inquiry.created_at)}</span>
                      <span>·</span>
                      <span className="flex items-center gap-0.5">
                        <Eye className="w-3 h-3 xxs:w-2.5 xxs:h-2.5" />
                        {inquiry.view_count ?? 0}
                      </span>
                      {(inquiry.comment_count ?? 0) > 0 && (
                        <span className={`flex items-center gap-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          <MessageCircle className="w-3 h-3 xxs:w-2.5 xxs:h-2.5" />
                          {inquiry.comment_count}
                        </span>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
